import React, { useState } from 'react';
import { traceDump as defaultDump, traceClear as defaultClear } from '../utils/traceLog';

/**
 * Admin-only "Copy diagnostics" (item 1, 2026-10-06). Shown on the Profile
 * page so a freeze can be captured after the fact, even after a reload: the
 * dump includes the PREVIOUS session's trace, which is where the evidence
 * lands when an incident ends in a reload (the 2026-10-06 Safari freeze's
 * trace was lost exactly that way). Also used on the offer page's
 * load-problem screen.
 *
 * The trace itself (utils/traceLog.js) is local-only, bounded (200 events,
 * 200 chars per field, oldest dropped) and sanitized (flat primitives only:
 * outcome codes, durations, labels; never tokens, user data or content).
 * Nothing is sent anywhere; this only copies it for the admin to paste.
 *
 * Clipboard writes can fail (permission, WKWebView, insecure context), so
 * the dump is always shown inline and selectable, and a failed copy says so.
 * The caller decides who sees this (render it for admins only).
 */
const AdminDiagnostics = ({ traceDump = defaultDump, traceClear = defaultClear, clipboard }) => {
  const [dump, setDump] = useState(null);
  const [status, setStatus] = useState(null); // null | 'copied' | 'copy_failed' | 'cleared'

  const copy = async () => {
    const text = traceDump();
    setDump(text);
    try {
      // Acquired INSIDE the try: the navigator.clipboard getter itself can
      // throw in some embedded browsers, not only writeText.
      const cb = clipboard !== undefined ? clipboard : (typeof navigator !== 'undefined' ? navigator.clipboard : undefined);
      if (!cb || typeof cb.writeText !== 'function') throw new Error('clipboard unavailable');
      await cb.writeText(text);
      setStatus('copied');
    } catch (e) {
      setStatus('copy_failed');
    }
  };

  const clear = () => {
    traceClear();
    setDump(null);
    setStatus('cleared');
  };

  const btn = { fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', background: 'none', border: '1px solid #9ca3af', borderRadius: '8px', padding: '0.35rem 0.9rem', cursor: 'pointer', color: '#4A5759' };

  return (
    <div data-testid="admin-diagnostics" style={{ marginTop: '1.5rem', textAlign: 'center' }}>
      <button type="button" onClick={copy} style={{ ...btn, marginRight: '0.5rem' }}>Copy diagnostics</button>
      <button type="button" onClick={clear} style={btn}>Clear</button>
      {status === 'copied' && <p role="status" style={{ fontSize: '0.8rem', color: '#4A5759', margin: '0.5rem 0 0' }}>Copied.</p>}
      {status === 'copy_failed' && (
        <p role="status" style={{ fontSize: '0.8rem', color: '#4A5759', margin: '0.5rem 0 0' }}>
          Couldn&rsquo;t copy automatically. Select the text below and copy it.
        </p>
      )}
      {status === 'cleared' && <p role="status" style={{ fontSize: '0.8rem', color: '#4A5759', margin: '0.5rem 0 0' }}>Cleared.</p>}
      {dump && (
        <pre data-testid="admin-diagnostics-dump" style={{ textAlign: 'left', fontSize: '10px', maxHeight: '220px', overflow: 'auto', background: '#f3f4f6', borderRadius: '8px', padding: '10px', marginTop: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all', userSelect: 'text' }}>
          {dump}
        </pre>
      )}
    </div>
  );
};

export default AdminDiagnostics;
